/**
 * Component Name: POGroupedTable
 * Description: A table component that groups invoices by purchase orders and displays aggregated data with sortable columns and clickable PO numbers.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to group by purchase orders.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - handlePOClick (function): Callback to handle clicks on PO numbers for drill-down.
 * Feature: Displays purchase order details, including procurement code, vendor, and invoice amounts, with clickable PO numbers to view associated invoices in the invoice management interface.
 */
import React from 'react';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal } from '../../util/decimal-utils';
import idx from 'idx';
import { format } from 'date-fns';
import { GSTORVAT } from '../../util/enums';
import {
    INVOICE_STATUS,
    INVOICE_TABLE_COLUMNS,
    INVOICE_CSS_CLASSES
} from '../../util/enums/invoice-management';

const POGroupedTable = ({ filteredInvoices, projects, handlePOClick }) => {
    // Group invoices by PurchaseOrders
    const groupedData = [];
    const poMap = new Map();

    filteredInvoices.forEach((inv) => {
        const poId = idx(inv, _ => inv.PurchaseOrders.id) || INVOICE_STATUS.NA;
        const procurementCode = idx(inv, _ => inv.PurchaseOrders.f_id || INVOICE_STATUS.NA);
        const project = projects.find((p) => p.id === inv.projectId);
        if (!poMap.has(poId)) {
            poMap.set(poId, {
                poId,
                procurementCode,
                projectId: inv.projectId,
                projectName: project?.name || INVOICE_STATUS.NA,
                sectionName: inv.sectionHierarchy || INVOICE_STATUS.NA,
                invoiceTotalAmount: toDecimal(0),
                invoiceSubTotal: toDecimal(0),
                invoiceCount: 0,
                vendor: inv.PurchaseOrders.vendor.business_name || INVOICE_STATUS.NA,
                poAddress: idx(inv, (_) => _.PurchaseOrders.address.join(', ')) || idx(inv, (_) => _.PurchaseOrders.vendor.address.replace(/ /g, ', ')) || INVOICE_STATUS.NA,
                poCity: inv.PurchaseOrders.city || INVOICE_STATUS.NA,
                poState: inv.PurchaseOrders.state || INVOICE_STATUS.NA,
                status: inv.PurchaseOrders.status || INVOICE_STATUS.NA,
                issueDate: inv.PurchaseOrders.createdAt ? format(new Date(inv.PurchaseOrders.createdAt), "dd/MM/yyyy") : "",
                invoiceStatus: inv.PurchaseOrders.invoicestatus || INVOICE_STATUS.NA,
                materialstatus: inv.PurchaseOrders.materialstatus || INVOICE_STATUS.NA,
                total: inv.PurchaseOrders.total || INVOICE_STATUS.NA,
            });
        }
        const po = poMap.get(poId);
        po.invoiceTotalAmount = po.invoiceTotalAmount.plus(toDecimal(inv.totalAmount || 0));
        po.invoiceSubTotal = po.invoiceSubTotal.plus(toDecimal(inv.subTotal || 0));
        po.invoiceCount += 1;
    });

    poMap.forEach((value, _) => {
        groupedData.push(value);
    });

    const columns = [
        {
            title: INVOICE_TABLE_COLUMNS.PO_NUMBER,
            field: 'poId',
            render: (row) => (
                <span className={INVOICE_CSS_CLASSES.PO_LINK} onClick={() => handlePOClick(row.poId, row.projectId, row.sectionName, row.procurementCode)}>
                    {/* procurementCode is the PO number with project prefix and Suffix if null then default to po Number*/}
                    {row.procurementCode || row.poId}
                </span>
            ),
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PROJECT_NAME,
            field: 'projectName',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.SECTION_SUBSECTION,
            field: 'sectionName',
            sortable: true,
            columnSearch: true
        },
        {
            title: INVOICE_TABLE_COLUMNS.VENDOR,
            field: 'vendor',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.ADDRESS,
            field: 'poAddress',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.STATUS,
            field: 'status',
            render: (rowData) => <span
                className={rowData.status === INVOICE_STATUS.PENDING ? INVOICE_CSS_CLASSES.TEXT_WARNING : rowData.status === INVOICE_STATUS.COMPLETED ? INVOICE_CSS_CLASSES.TEXT_SUCCESS : ''}>
                {rowData.status}
            </span>,
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.ISSUE_DATE,
            field: 'issueDate',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_TOTAL,
            field: 'total',
            class: 'text-right justify-content-end',
            render: (row) => toUSD(row.total),
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.MATERIAL_STATUS,
            field: 'materialstatus',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_AMOUNT,
            field: 'invoiceTotalAmount',
            class: 'text-right justify-content-end',
            render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.invoiceTotalAmount)}</span>,
            sortable: true,
            columnSearch: true,
        },
        {
            title: `Invoice SubTotal (Ex. ${GSTORVAT})`,
            field: 'invoiceSubTotal',
            class: 'text-right justify-content-end',
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.invoiceSubTotal)}</span>,
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_COUNT,
            field: 'invoiceCount',
            class: 'text-right justify-content-end',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_STATUS,
            field: 'invoiceStatus',
            sortable: true,
            columnSearch: true,
        }
    ];


    const tableItemClickHandler = (row) => {
        handlePOClick(row.poId, row.projectId, row.sectionName, row.procurementCode);
    };

    return (
        <div className="table-container">
            <CustomTable columns={columns} data={groupedData} pagination={true} rowClickHandler={tableItemClickHandler} />
        </div>
    );
};

export default POGroupedTable;